import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsomorphicLayoutEffect } from "@/lib/useIsomorphicLayoutEffect";
import { useRouter } from "next/router";
import Head from "next/head";
import Script from "next/script";
import { SidebarProvider } from "@/context/sidebarContext";
import { pushEventRemoveCart } from "@/utils/dataLayer";
import { checkProductListCacheVersion } from "@/utils/cacheUtils";
import { getApiBaseUrl } from "@/lib/apiUrl";
import { isNavDebugEnabled, navDebugLog } from "@/lib/navDebug";
import { ensureGooberStyleTextNode } from "@/lib/ensureGooberStyleNode";
import { installBackendAxiosAuth } from "@/lib/installBackendAxiosAuth";
import { loadAxios } from "@/lib/loadAxios";

if (typeof window !== "undefined") {
  installBackendAxiosAuth();
}
import {
  calculateSurcharge,
  getBuyerCountryString,
  getCartUnitPriceCad,
  refreshCartLineProductData,
} from "@/utils/pricingUtils";
import {
  appointmentSlotKeyFromHold,
  appointmentSlotKeyFromLine,
  buildServiceCartLineFromHold,
  isServiceAppointmentLine,
  resolveServiceCartLineId,
} from "@/lib/serviceCartLine";
import { releaseServiceBookingHold } from "@/lib/serviceBookingApi";
import { appShellFontVariablesClassName } from "@/components/appFonts";
import { SHOW_CHATBOT } from "@/lib/featureFlags";
import { MountThirdwebContext } from "@/context/MountThirdwebContext";
import dynamic from "next/dynamic";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import FramerLazyMotionRoot from "@/components/FramerLazyMotionRoot";

/** Load thirdweb only after `requestMountThirdweb()` — static import runs on every page and can throw before any error boundary. */
const ThirdwebShell = dynamic(() => import("@/components/ThirdwebShell"), { ssr: false });

const NavigationLoader = dynamic(() => import("@/components/NavigationLoader"), { ssr: false });
const FirstOrderDiscountPopup = dynamic(() => import("@/components/FirstOrderDiscountPopup"), { ssr: false });
const AfomaChatbot = dynamic(() => import("@/components/AfomaChatbot"), { ssr: false });
const CookieConsent = dynamic(() => import("@/components/CookieConsent"), {
  ssr: false,
  loading: () => null,
});
const Toaster = dynamic(
  () => import("react-hot-toast").then((m) => m.Toaster),
  { ssr: false }
);

const SESSION_TIMEOUT = 1 * 60 * 60 * 1000; // 1 hour in milliseconds

const notifySuccess = async (message) => {
  const { toast } = await import("react-hot-toast");
  toast.success(message);
};

export default function AppShell({ Component, pageProps }) {
  const router = useRouter();

  useEffect(() => {
    if (!isNavDebugEnabled()) return undefined;
    navDebugLog("AppShell render route", {
      pathname: router.pathname,
      asPath: router.asPath,
      query: router.query,
    });
  }, [router.pathname, router.asPath, router.query]);

  useEffect(() => {
    if (!isNavDebugEnabled()) return undefined;
    const onStart = (url) => navDebugLog("routeChangeStart", url);
    const onDone = (url) => navDebugLog("routeChangeComplete", url);
    const onErr = (err, url) => navDebugLog("routeChangeError", { url, err: String(err) });
    router.events.on("routeChangeStart", onStart);
    router.events.on("routeChangeComplete", onDone);
    router.events.on("routeChangeError", onErr);
    return () => {
      router.events.off("routeChangeStart", onStart);
      router.events.off("routeChangeComplete", onDone);
      router.events.off("routeChangeError", onErr);
    };
  }, [router]);

  const [mountThirdweb, setMountThirdweb] = useState(false);
  const mountThirdwebResolvers = useRef([]);
  /** Side effects after setCart (toast/saveCart must not run inside the updater — Strict Mode runs it twice). */
  const appointmentCartEffectRef = useRef(null);
  /** Preload chunk before flipping `mountThirdweb` so ThirdwebProvider exists when wallet UI renders. */
  const requestMountThirdweb = useCallback(async () => {
    if (mountThirdweb) {
      return;
    }
    try {
      await import("@/components/ThirdwebShell");
    } catch (e) {
      console.error("Thirdweb bundle failed to load:", e);
      return;
    }
    await new Promise((resolve) => {
      mountThirdwebResolvers.current.push(resolve);
      setMountThirdweb(true);
    });
  }, [mountThirdweb]);

  useEffect(() => {
    if (!mountThirdweb) return;
    const res = mountThirdwebResolvers.current;
    mountThirdwebResolvers.current = [];
    res.forEach((r) => r());
  }, [mountThirdweb]);

  const mountThirdwebContextValue = useMemo(
    () => ({
      thirdwebUIReady: mountThirdweb,
      requestMountThirdweb,
    }),
    [mountThirdweb, requestMountThirdweb]
  );
  const [cart, setCart] = useState({});
  const saveCartRef = useRef(null);
  const [subTotal, setSubTotal] = useState(0);
  const [userInfo, setUserInfo] = useState({});
  const [currency, setCurrency] = useState("");
  const [totalShippingRate, setTotalShippingRate] = useState(0);
  const [fetchedShippingRate, setFetchedShippingRate] = useState(0);
  const [cookiePrefs, setCookiePrefs] = useState(null);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);
  /** When false, cookie chunk is not loaded — avoids cookie copy becoming late LCP on sign-in (mobile). */
  const [cookieConsentMounted, setCookieConsentMounted] = useState(false);
  const [showDeferredWidgets, setShowDeferredWidgets] = useState(false);
  const [analyticsScriptsReady, setAnalyticsScriptsReady] = useState(false);
  const [clientRuntimeError, setClientRuntimeError] = useState(null);
  useEffect(() => {
    const scrollMainToTop = () => {
      if (typeof window === "undefined") return;
      window.scrollTo(0, 0);
      document.getElementById("main-content")?.scrollTo?.(0, 0);
    };
    /** Cart/modals/selects sometimes set `overflow: hidden` on `body`/`html` without cleaning up. */
    const clearGlobalScrollLock = () => {
      if (typeof document === "undefined") return;
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      document.body.classList.remove("cart-open");
    };
    const onRouteChangeStart = () => {
      setShowCookieModal(false);
      clearGlobalScrollLock();
    };

    router.events.on("routeChangeStart", onRouteChangeStart);
    router.events.on("routeChangeComplete", scrollMainToTop);

    return () => {
      router.events.off("routeChangeStart", onRouteChangeStart);
      router.events.off("routeChangeComplete", scrollMainToTop);
    };
  }, [router]);

  useEffect(() => {
    ensureGooberStyleTextNode();
  }, []);

  // Same logic as homepage: check product-list cache version so home, category, subcategory, shop all
  // get productListCacheInvalidated when backend revalidates (product create/update/status/stock).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const apiBase = getApiBaseUrl();
    if (!apiBase) return;
    const check = () => checkProductListCacheVersion(apiBase).catch(() => {});
    // Defer first check so it does not compete with LCP hero / first-paint network on home.
    let cancelFirstRun = null;
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(() => check(), { timeout: 6000 });
      cancelFirstRun = () => window.cancelIdleCallback(id);
    } else {
      const id = window.setTimeout(check, 1500);
      cancelFirstRun = () => clearTimeout(id);
    }
    const interval = setInterval(check, 60 * 1000); // every 60s so open category/shop tabs see updates
    return () => {
      clearInterval(interval);
      cancelFirstRun?.();
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let done = false;
    const enableWidgets = () => {
      if (done) return;
      done = true;
      window.removeEventListener("pointerdown", enableWidgets, true);
      window.removeEventListener("keydown", enableWidgets, true);
      window.removeEventListener("touchstart", enableWidgets, true);
      window.removeEventListener("scroll", enableWidgets, true);
      setShowDeferredWidgets(true);
    };
    window.addEventListener("pointerdown", enableWidgets, true);
    window.addEventListener("keydown", enableWidgets, true);
    window.addEventListener("touchstart", enableWidgets, true);
    window.addEventListener("scroll", enableWidgets, true);
    return () => {
      window.removeEventListener("pointerdown", enableWidgets, true);
      window.removeEventListener("keydown", enableWidgets, true);
      window.removeEventListener("touchstart", enableWidgets, true);
      window.removeEventListener("scroll", enableWidgets, true);
    };
  }, []);

  // Defer analytics until first interaction (keeps main thread freer on first paint) or a short
  // idle fallback so passive visits / GA debug still receive page_view after consent.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!cookiePrefs?.analytics) {
      setAnalyticsScriptsReady(false);
      return;
    }
    let done = false;
    let fallbackTimer = null;
    const markReady = () => {
      if (done) return;
      done = true;
      window.removeEventListener("pointerdown", markReady, true);
      window.removeEventListener("keydown", markReady, true);
      window.removeEventListener("touchstart", markReady, true);
      window.removeEventListener("scroll", markReady, true);
      if (fallbackTimer != null) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      setAnalyticsScriptsReady(true);
    };
    window.addEventListener("pointerdown", markReady, true);
    window.addEventListener("keydown", markReady, true);
    window.addEventListener("touchstart", markReady, true);
    window.addEventListener("scroll", markReady, true);
    fallbackTimer = window.setTimeout(markReady, 4500);
    return () => {
      window.removeEventListener("pointerdown", markReady, true);
      window.removeEventListener("keydown", markReady, true);
      window.removeEventListener("touchstart", markReady, true);
      window.removeEventListener("scroll", markReady, true);
      if (fallbackTimer != null) window.clearTimeout(fallbackTimer);
    };
  }, [cookiePrefs?.analytics]);

  // NEXT_PUBLIC_* is inlined at build time. A staging bundle built without NEXT_PUBLIC_BASE_URL
  // will call `undefined/...` URLs — works locally if .env exists only on your machine.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const base = process.env.NEXT_PUBLIC_BASE_URL;
    if (!base || typeof base !== "string" || !/^https?:\/\//i.test(base.trim())) {
      console.warn(
        "[AFOMA] NEXT_PUBLIC_BASE_URL is missing or invalid in this client bundle. " +
          "Set it in the environment used for `next build` (staging CI must inject it before build)."
      );
    }
  }, []);

  // `AppErrorBoundary` only catches render-phase errors. Logged-out async / import failures
  // often surface as unhandledrejection or global `error` on Next chunks — show recovery UI.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ignore = (msg) =>
      /Receiving end does not exist|ResizeObserver loop|Non-Error promise rejection|paypal_js_sdk/i.test(
        String(msg || "")
      );
    const toCapturedError = (r) => {
      if (r instanceof Error) return r;
      if (typeof r === "string") return new Error(r);
      if (typeof r === "object" && r !== null) {
        try {
          const s = JSON.stringify(r);
          return new Error(s === "{}" ? String(r) : s);
        } catch {
          return new Error(String(r));
        }
      }
      return new Error(String(r));
    };
    const onRejection = (ev) => {
      const r = ev.reason;
      const msg = r?.message ?? String(r ?? "");
      if (ignore(msg)) return;
      const err = toCapturedError(r);
      // Always log full stack — production is minified unless you build with browser source maps.
      console.error("[AFOMA] unhandledrejection", err.message, err.stack || err);
      setClientRuntimeError(err);
    };
    const onWindowError = (ev) => {
      if (ignore(String(ev?.message || "")) || ignore(String(ev?.error?.message || ""))) return;
      const err = ev.error;
      if (!err) return;
      const fn = ev.filename || "";
      if (fn && !fn.includes("_next") && !fn.includes("webpack-internal")) return;
      const captured = err instanceof Error ? err : new Error(String(err));
      console.error("[AFOMA] window error", captured.message, captured.stack || captured, ev.filename, ev.lineno);
      setClientRuntimeError(captured);
    };
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("error", onWindowError);
    return () => {
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("error", onWindowError);
    };
  }, []);

  // Do not auto-mount Thirdweb globally on first paint (can hurt slow/staging). Header wallet
  // calls `requestMountThirdweb()`. Sign-in Register wraps only the embed in `ThirdwebShell` so
  // switching tabs does not remount the whole app.

  let timeout;

  // Function to clear all data from localStorage except for cart
  const clearUserLocalStorage = () => {
    const savedCart = localStorage.getItem("cart");
    localStorage.clear();
    if (savedCart) {
      localStorage.setItem("cart", savedCart);
    }
  };

  const saveCartInDb = async (
    newCart,
    subTotal,
    totalShippingRate,
    fetchedShippingRate
  ) => {
    let user = null;
    try {
      const raw = localStorage.getItem("user");
      user = raw ? JSON.parse(raw) : null;
    } catch {
      user = null;
    }
    if (user?.userId) {
      const axios = await loadAxios();
      await axios.post(
        `${process.env.NEXT_PUBLIC_BASE_URL}/cart/add-cart`,
        {
          user_id: user?.userId,
          cart: newCart,
          subTotal: subTotal,
          totalShippingRate: totalShippingRate,
          fetchedShippingRate: fetchedShippingRate,
        },
        {
          headers: {
            "x-api-key": process.env.NEXT_PUBLIC_API_KEY,
          },
        }
      );
    }
  };
  const syncCartLinePrices = (cartObj, userInfoForPricing) => {
    const keys = Object.keys(cartObj);
    for (let i = 0; i < keys.length; i++) {
      const item = cartObj[keys[i]];
      if (!item?.productData) continue;
      item.productData = refreshCartLineProductData(
        item.productData,
        userInfoForPricing
      );
      const qty = parseFloat(item.orderQuantiy) || 1;
      const unitCad = getCartUnitPriceCad(
        item.productData,
        item.selectedVariations
      );
      item.basePrice = unitCad;
      item.totalAmount = applyDiscount(item, unitCad * qty, keys.length);
    }
    return cartObj;
  };

  /**
   * @param {object} newCart
   * @param {{ persistRemote?: boolean }} [options] persistRemote=false skips /cart/add-cart (use on hydrate only).
   */
  const saveCart = async (newCart, options = {}) => {
    const persistRemote = options.persistRemote !== false;
    let userInfoForCart = userInfo;
    if (typeof window !== "undefined") {
      try {
        userInfoForCart =
          JSON.parse(localStorage.getItem("userInfo") || "{}") || userInfo;
      } catch {
        userInfoForCart = userInfo;
      }
    }
    syncCartLinePrices(newCart, userInfoForCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
    let subt = 0;
    let shippingRate = 0;
    let keys = Object.keys(newCart);
    let oldSubTotal = 0
    const sellerShippingRate = getShippingRatesBySeller(newCart)
      ? getShippingRatesBySeller(newCart)
      : undefined;
    for (let i = 0; i < keys.length; i++) {
      subt += applyDiscount(
        newCart[keys[i]],
        parseFloat(newCart[keys[i]].basePrice) *
          parseFloat(newCart[keys[i]].orderQuantiy),
          keys.length
      );
      oldSubTotal += parseFloat(newCart[keys[i]].basePrice) *
          parseFloat(newCart[keys[i]].orderQuantiy)
      // newCart[keys[i]].productData.surTotalAmount = applyDiscount(
      //   newCart[keys[i]],
      //   parseFloat(newCart[keys[i]].productData.surTotalAmount) *
      //     parseFloat(newCart[keys[i]].orderQuantiy)
      // );
    }
    if(oldSubTotal > subt){
      localStorage.setItem("oldSubTotal", oldSubTotal)
    }
    if (sellerShippingRate && sellerShippingRate.length) {
      for (let i = 0; i < sellerShippingRate.length; i++) {
        subt += (sellerShippingRate[i].deductedAm) || 0;
        shippingRate += sellerShippingRate[i].shippingRate
          ? sellerShippingRate[i].shippingRate
          : 0;
      }
    }
    if (
      keys.length &&
      newCart[keys[0]].shippingOptions?.length &&
      userInfo?.currency &&
      userInfo?.currencyRate
    ) {
      var fetchedShippingRate = shippingRate / (userInfo?.currencyRate || 1);
      localStorage.setItem(
        "fetchedShippingRate",
        fetchedShippingRate.toFixed(2)
      );
      setFetchedShippingRate(fetchedShippingRate.toFixed(2));
    } else {
      localStorage.setItem("fetchedShippingRate", shippingRate.toFixed(2));
      setFetchedShippingRate(shippingRate.toFixed(2));
    }

    localStorage.setItem("totalShippingRate", shippingRate.toFixed(2));
    localStorage.setItem("subTotal", subt);
    setSubTotal(subt);
    setTotalShippingRate(shippingRate.toFixed(2));
    setCart(newCart);
    if (persistRemote) {
      await saveCartInDb(
        newCart,
        subt,
        shippingRate.toFixed(2),
        fetchedShippingRate?.toFixed(2)
      );
    }
  };

  saveCartRef.current = saveCart;

  const releaseAppointmentHoldForLine = useCallback((line) => {
    const ref = line?.bookingDetails?.bookingRef;
    if (!ref) return;
    let token;
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      token = user?.accessToken || user?.token;
    } catch {
      token = undefined;
    }
    void releaseServiceBookingHold(ref, token).catch(() => {});
  }, []);

  const releaseAppointmentHoldsInCart = useCallback(
    (cartObj) => {
      if (!cartObj || typeof cartObj !== "object") return;
      for (const line of Object.values(cartObj)) {
        if (isServiceAppointmentLine(line)) {
          releaseAppointmentHoldForLine(line);
        }
      }
    },
    [releaseAppointmentHoldForLine]
  );

  useIsomorphicLayoutEffect(() => {
    const pending = appointmentCartEffectRef.current;
    if (!pending) return;
    appointmentCartEffectRef.current = null;
    void saveCartRef.current?.(pending.cart);
    void notifySuccess(
      pending.isUpdate
        ? "Appointment updated in cart."
        : "Appointment added to cart."
    );
  }, [cart]);

  /** `saveCart` writes these keys; React state was only updated on cart mutations, so a full reload or fast navigation left totals at 0 until checkout re-read localStorage. */
  const hydrateCartTotalsFromStorage = useCallback(() => {
    if (typeof window === "undefined") return;
    const readNum = (key) => {
      const raw = localStorage.getItem(key);
      if (raw == null || raw === "") return null;
      try {
        const v = JSON.parse(raw);
        if (typeof v === "number" && Number.isFinite(v)) return v;
        const n = parseFloat(String(v));
        return Number.isFinite(n) ? n : null;
      } catch {
        const n = parseFloat(raw);
        return Number.isFinite(n) ? n : null;
      }
    };
    const st = readNum("subTotal");
    const ts = readNum("totalShippingRate");
    const fs = readNum("fetchedShippingRate");
    if (st !== null) setSubTotal(st);
    if (ts !== null) setTotalShippingRate(Number(ts).toFixed(2));
    if (fs !== null) setFetchedShippingRate(Number(fs).toFixed(2));
  }, []);

  const getShippingRatesBySeller = (data) => {
    const groupedData = groupProductsBySeller(data);
    const shippingRatesBySeller = [];

    for (const sellerId in groupedData) {
      const { shippingRate, deductedAm } = getShippingRate(
        groupedData[sellerId]
      );
      shippingRatesBySeller.push({ sellerId, shippingRate, deductedAm });
    }

    return shippingRatesBySeller;
  };

  const getShippingRate = (products) => {
    let storedUserInfo = userInfo;
    try {
      storedUserInfo =
        JSON.parse(localStorage.getItem("userInfo") || "{}") || userInfo;
    } catch {
      storedUserInfo = userInfo;
    }
    const buyerCountry = getBuyerCountryString(storedUserInfo);
    const userSurcharge = storedUserInfo?.surCharge || {};
    let surcharge = 0;
    for(const item of products) {
      if (item.productData?.variations?.length) {
        let selectedVariant = findSelectedVariation(
          item.productData?.variations,
          item.selectedVariations
        );
        if (selectedVariant?.surTotalAmount != null) {
          item.productData.surTotalAmount = selectedVariant.surTotalAmount;
        }
      }
    }
    for (const item of products) {
      const productData = item?.productData;
      if (!productData) continue;

      const sellerCountry = productData.seller?.country?.trim() || "";
      const productType = productData.productType;
      const skipsShipping =
        productType === "Downloadable" || productType === "Service";

      if (
        !skipsShipping &&
        productData.seller?.shippingConfigId?.international?.afoma_shipping &&
        sellerCountry !== buyerCountry
      ) {
        surcharge =
          userSurcharge[`${sellerCountry}-${buyerCountry}`] || 0;
      }
      if (item.shippingRate > 0) {
        return {
          shippingRate: item.shippingRate,
          deductedAm: (surcharge * ((item.orderQuantiy - 1) + products.length)),
        };
      } else {
        return {
          shippingRate: undefined,
          deductedAm: (surcharge * ((item.orderQuantiy - 1) + products.length)),
        };
      }
    }
    return 0; // Default to 0 if no positive shippingRate is found
  };

  const findSelectedVariation = (variations, selectedVariations) => {
    return variations.find((variation) => {
      return selectedVariations.every(
        (selected) =>
          variation[selected.attributeName] === selected.attributeValue
      );
    });
  };

  const groupProductsBySeller = (data) => {
    const groupedData = {};
    for (const key in data) {
      const item = data[key];
      const sellerId = item.productData?.seller?._id;
      if (!sellerId) continue;
      if (!groupedData[sellerId]) {
        groupedData[sellerId] = [];
      }
      groupedData[sellerId].push(item);
    }
    return groupedData;
  };

  // Function to handle adding items to cart
  const addToCart = (
    productId,
    orderQuantiy,
    maxQuantity,
    basePrice,
    productData,
    remark,
    shippingOptions,
    shippingService,
    shippingRate,
    selectedVariations,
    bookingDetails
  ) => {
    let userInfo = {};
    try {
      userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}") || {};
    } catch {
      userInfo = {};
    }
    setUserInfo(userInfo);
    findCurrency(userInfo);
    const pricedProduct =
      calculateSurcharge([productData])?.[0] ?? productData;
    const unitCad =
      getCartUnitPriceCad(pricedProduct, selectedVariations) ||
      parseFloat(basePrice) ||
      0;
    /** Functional update so back-to-back updates (e.g. cart page rate fetch per line) do not drop prior lines. */
    setCart((prev) => {
      const safeCart =
        prev && typeof prev === "object" && !Array.isArray(prev) ? prev : {};
      let newCart = JSON.parse(JSON.stringify(safeCart));
      let keys = Object.keys(newCart);
      if (Object.prototype.hasOwnProperty.call(newCart, productId)) {
        newCart[productId].orderQuantiy = orderQuantiy;
        newCart[productId].productData = pricedProduct;
        newCart[productId].basePrice = unitCad;
        newCart[productId].totalAmount = applyDiscount(
          newCart[productId],
          unitCad * orderQuantiy,
          keys.length
        );
        newCart[productId].shippingOptions = shippingOptions;
        newCart[productId].shippingService = shippingService;
        newCart[productId].shippingRate = shippingRate;
        if (bookingDetails) {
          newCart[productId].bookingDetails = bookingDetails;
        }
      } else {
        newCart[productId] = {
          orderQuantiy: orderQuantiy,
          totalAmount: applyDiscount(
            { productData: pricedProduct },
            parseFloat(orderQuantiy) * unitCad,
            1
          ),
          selectedVariations,
          productData: pricedProduct,
          maxQuantity,
          basePrice: unitCad,
          remark,
          shippingOptions,
          shippingService,
          shippingRate,
          ...(bookingDetails ? { bookingDetails } : {}),
        };
        if (!String(productId).startsWith("svc:")) {
          void notifySuccess("Item Added!");
        }
      }
      saveCart(newCart);
      return newCart;
    });
  };

  const addServiceAppointmentToCart = (hold, service) => {
    if (!hold?.startsAt || !service?._id) return;

    let userInfo = {};
    try {
      userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}") || {};
    } catch {
      userInfo = {};
    }
    setUserInfo(userInfo);
    findCurrency(userInfo);

    const lineId = resolveServiceCartLineId(hold, service);
    if (!lineId) return;
    const slotKey = appointmentSlotKeyFromHold(hold, service);
    const pricedProduct = calculateSurcharge([service])?.[0] ?? service;
    const unitCad =
      getCartUnitPriceCad(pricedProduct) ||
      parseFloat(hold.priceSnapshot?.amount) ||
      parseFloat(pricedProduct.finalPrice || pricedProduct.price) ||
      0;

    setCart((prev) => {
      const safeCart =
        prev && typeof prev === "object" && !Array.isArray(prev) ? { ...prev } : {};

      if (slotKey) {
        for (const [existingId, existingLine] of Object.entries(safeCart)) {
          if (existingId === lineId || !isServiceAppointmentLine(existingLine)) continue;
          if (appointmentSlotKeyFromLine(existingLine) !== slotKey) continue;
          releaseAppointmentHoldForLine(existingLine);
          delete safeCart[existingId];
        }
      }

      const line = buildServiceCartLineFromHold(hold, pricedProduct);
      line.productData = pricedProduct;
      line.basePrice = unitCad;
      line.totalAmount = applyDiscount(
        { productData: pricedProduct },
        unitCad,
        Object.keys(safeCart).length + (safeCart[lineId] ? 0 : 1)
      );

      const isUpdate = Boolean(safeCart[lineId]);
      safeCart[lineId] = line;
      appointmentCartEffectRef.current = { cart: safeCart, isUpdate };
      return safeCart;
    });
  };

  const applyDiscount = (product, amount, cartLength) => {
    const appliedCoupon = JSON.parse(localStorage.getItem("appliedCoupon"));
    const sellerId =
      product &&
      product.productData &&
      product.productData.seller &&
      product.productData.seller.userId
        ? product.productData.seller.userId
        : undefined;
    if (
      (appliedCoupon &&
        appliedCoupon.couponCode &&
        appliedCoupon.discountAmount &&
        appliedCoupon.createdBy &&
        appliedCoupon.createdBy._id &&
        sellerId &&
        appliedCoupon.createdBy._id == sellerId) ||
      (appliedCoupon &&
        appliedCoupon.couponCode &&
        appliedCoupon.discountAmount &&
        appliedCoupon.createdBy &&
        appliedCoupon.createdBy.userRole &&
        appliedCoupon.createdBy.userRole === "admin") ||
        (appliedCoupon &&
        appliedCoupon.couponCode &&
        appliedCoupon.discountAmount &&
        appliedCoupon.createdBy &&
        appliedCoupon.createdBy.userRole &&
        appliedCoupon.createdBy.userRole === "affiliate")
    ) {
      if (appliedCoupon.couponType == "percentage") {
        const discountAmount = parseFloat(appliedCoupon.discountAmount);
        const discountedAmount =
          parseFloat(amount) - (parseFloat(amount) * discountAmount) / 100;
        return discountedAmount;
      }
      else{
        return  (parseFloat(amount) - (appliedCoupon.discountAmount / (cartLength ? cartLength : 1)))
      }

    }
    return parseFloat(amount);
  };

  // Function to handle removing items from cart
  const removeFromCart = (productId, orderQuantiy) => {
    const newCart = JSON.parse(JSON.stringify(cart));
    const keys = Object.keys(newCart);
    const line = newCart[productId];
    if (!line) return;

    const deduct = Math.max(1, Number(orderQuantiy) || 1);
    const currentQty = Number(line.orderQuantiy) || 0;

    // Last unit (or remove >= qty): drop the line — previously only `orderQuantiy > 1` ran, so minus on qty 1 did nothing.
    if (currentQty <= deduct) {
      if (line.productData?.couponCode) {
        localStorage.removeItem("appliedCoupon");
        localStorage.removeItem("oldSubTotal");
      }
      if (isServiceAppointmentLine(line)) {
        releaseAppointmentHoldForLine(line);
      }
      pushEventRemoveCart(line);
      delete newCart[productId];
      if (!Object.keys(newCart).length) {
        localStorage.removeItem("oldSubTotal");
      }
      setCart(newCart);
      saveCart(newCart);
      void notifySuccess("Item Removed!");
      return;
    }

    line.orderQuantiy = currentQty - deduct;
    line.totalAmount = applyDiscount(
      line,
      parseFloat(line.basePrice) * parseFloat(line.orderQuantiy),
      keys.length
    );
    setCart(newCart);
    saveCart(newCart);
  };

  // Function to clear the entire cart
  const clearCart = () => {
    releaseAppointmentHoldsInCart(cart);
    // localStorage.removeItem("appliedCoupon");
    localStorage.removeItem("selected-delivery-address");
    localStorage.removeItem("appliedCoupon");
    localStorage.removeItem("oldSubTotal");
    setCart({});
    saveCart({});
  };

  // Function to delete a specific item from cart
  const deleteFromCart = (productId) => {
    let newCart = JSON.parse(JSON.stringify(cart));
    if (productId in cart) {
      if (
        newCart[productId] &&
        newCart[productId].productData &&
        newCart[productId].productData.couponCode
      ) {
        localStorage.removeItem("appliedCoupon");
        localStorage.removeItem("oldSubTotal");
      }
      if (isServiceAppointmentLine(newCart[productId])) {
        releaseAppointmentHoldForLine(newCart[productId]);
      }
      pushEventRemoveCart(newCart[productId])
      delete newCart[productId];
    }
    if(!Object.keys(newCart).length){
        localStorage.removeItem("oldSubTotal");
    }
    setCart(newCart);
    saveCart(newCart);
    void notifySuccess("Item Removed!");
  };

  const findCurrency = (userInfo) => {
    const payPalBanCountries = ["Pakistan", "Nigeria"];
    if (payPalBanCountries.some((country) => country == userInfo.country)) {
      setCurrency("USD");
    } else {
      setCurrency(userInfo.currency);
    }
  };

  // ---- Cookie consent helpers ----
  const loadCookiePrefs = () => {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem("cookiePreferences");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const persistCookiePrefs = (prefs) => {
    const value = {
      essential: true,
      analytics: !!prefs.analytics,
      marketing: !!prefs.marketing,
      updatedAt: new Date().toISOString(),
    };
    setCookiePrefs(value);
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("cookiePreferences", JSON.stringify(value));
      } catch {
        // ignore storage errors
      }
    }
  };

  const handleAcceptAllCookies = () => {
    persistCookiePrefs({ analytics: true, marketing: true });
    setShowCookieBanner(false);
    setShowCookieModal(false);
  };

  const handleRejectNonEssentialCookies = () => {
    persistCookiePrefs({ analytics: false, marketing: false });
    setShowCookieBanner(false);
    setShowCookieModal(false);
  };

  const handleSaveCookiePreferences = (prefs) => {
    persistCookiePrefs(prefs);
    setShowCookieBanner(false);
    setShowCookieModal(false);
  };
  useEffect(() => {
    if (typeof window === "undefined" || window.__thirdwebSocialFetchPatched) return;
    // Prevent noisy thirdweb social profile retries from running on every page lifecycle.
    const originalFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === "string" ? input : input?.url;
      if (url && url.includes("social.thirdweb.com") && url.includes("/v1/profiles/")) {
        return Promise.resolve(
          new Response(JSON.stringify({ data: [] }), {
            status: 200,
            headers: { "Content-Type": "application/json" },
          })
        );
      }
      return originalFetch.call(this, input, init);
    };
    window.__thirdwebSocialFetchPatched = true;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || window.__paypalErrorHandlerPatched) return;
    // Suppress known harmless PayPal postMessage timeout noise.
    const isPayPalPostMessageError = (msg) =>
      typeof msg === "string" &&
      (msg.includes("No ack for postMessage") || msg.includes("paypal_js_sdk_v5_unhandled_exception"));
    const onError = (ev) => {
      if (isPayPalPostMessageError(ev.message)) {
        ev.preventDefault();
        ev.stopPropagation();
        return true;
      }
    };
    const onUnhandledRejection = (ev) => {
      const msg = ev.reason?.message ?? ev.reason?.err ?? String(ev.reason);
      if (isPayPalPostMessageError(msg)) {
        ev.preventDefault();
        ev.stopPropagation();
        return true;
      }
    };
    const onPayPalUnhandled = (ev) => {
      const msg = ev.detail?.err ?? ev.detail?.message ?? "";
      if (isPayPalPostMessageError(msg)) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    };
    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onUnhandledRejection, true);
    window.addEventListener("paypal_js_sdk_v5_unhandled_exception", onPayPalUnhandled, true);
    window.__paypalErrorHandlerPatched = true;
    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onUnhandledRejection, true);
      window.removeEventListener("paypal_js_sdk_v5_unhandled_exception", onPayPalUnhandled, true);
    };
  }, []);

  useEffect(() => {
    // Defer geoIP until post-interaction (or late fallback) to reduce mobile startup work.
    let geoKickoffDone = false;
    let geoFallbackTimer;
    let cookieBannerIdleId;
    let cookieBannerTimerId;
    /** Only set on /checkout when we defer cookie banner until `load`. */
    let cookieCheckoutLoadHandler = null;
    const kickoffGeo = () => {
      if (geoKickoffDone) return;
      geoKickoffDone = true;
      window.removeEventListener("pointerdown", kickoffGeo, true);
      window.removeEventListener("keydown", kickoffGeo, true);
      window.removeEventListener("touchstart", kickoffGeo, true);
      window.removeEventListener("scroll", kickoffGeo, true);
      if (geoFallbackTimer) clearTimeout(geoFallbackTimer);
      void import("@/lib/geoIP").then((m) => m.getGeoIP());
    };
    window.addEventListener("pointerdown", kickoffGeo, true);
    window.addEventListener("keydown", kickoffGeo, true);
    window.addEventListener("touchstart", kickoffGeo, true);
    window.addEventListener("scroll", kickoffGeo, true);
    geoFallbackTimer = setTimeout(kickoffGeo, 12000);
    const userInfo = JSON.parse(localStorage.getItem("userInfo")) || {};
    setUserInfo(userInfo);
    findCurrency(userInfo);
    // Re-price cart after paint (flat-rate handling) without blocking first paint
    const loadedCart = JSON.parse(localStorage.getItem("cart"));
    if (loadedCart && Object.keys(loadedCart).length > 0) {
      const repriceCart = () => void saveCart(loadedCart, { persistRemote: false });
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(repriceCart, { timeout: 2500 });
      } else {
        window.setTimeout(repriceCart, 0);
      }
    } else if (loadedCart) {
      setCart(loadedCart);
    }
    hydrateCartTotalsFromStorage();

    // Function to clear timeout and start a new one
    const resetTimeout = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        clearUserLocalStorage(); // Clear user data from localStorage
        window.location.href = "/sign-in"; // Redirect to sign-in page after clearing data
      }, SESSION_TIMEOUT);
    };

    // Set initial timeout when the app loads
    resetTimeout();

    // Reset timeout on route change
    const handleRouteChange = () => {
      resetTimeout();
    };
    // Add event listener to handle user login
    const handleUserLogin = (event) => {
      const userInfo = JSON.parse(localStorage.getItem("userInfo")) || {};
      setUserInfo(userInfo);
      findCurrency(userInfo);
      // Load cart data from localStorage on initial mount
      const loadedCart = JSON.parse(localStorage.getItem("cart"));
      if (loadedCart && Object.keys(loadedCart)?.length > 0) {
        void saveCart(loadedCart, { persistRemote: false });
      }
      hydrateCartTotalsFromStorage();
      if (event.detail) {
        getCartFromDB();
      }
    };
    window.addEventListener("userLoggedIn", handleUserLogin);

    const handleCartPricesSynced = () => {
      try {
        const syncedCart = JSON.parse(localStorage.getItem("cart") || "{}");
        if (syncedCart && Object.keys(syncedCart).length > 0) {
          void saveCart(syncedCart, { persistRemote: false });
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener("cartPricesSynced", handleCartPricesSynced);

    // Cookie preferences: load on first mount
    const existingPrefs = loadCookiePrefs();
    if (existingPrefs) {
      setCookiePrefs(existingPrefs);
      setCookieConsentMounted(true);
    } else {
      const showBannerSoon = () => {
        setCookieConsentMounted(true);
        setShowCookieBanner(true);
      };
      const path =
        typeof window !== "undefined" ? window.location.pathname : "";
      const onCheckout = path === "/checkout";
      const onHome = path === "/";
      // After paint: main content can win LCP. Checkout + home: defer cookie chunk so hero/summary stay primary.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (onCheckout) {
            // Checkout: wait for full load + idle so the cookie strip is not the LCP element and
            // PayPal/order UI paints first (Lighthouse “element render delay” on cookie copy).
            const scheduleCheckoutBanner = () => {
              if (typeof window !== "undefined" && "requestIdleCallback" in window) {
                cookieBannerIdleId = window.requestIdleCallback(showBannerSoon, {
                  timeout: 8000,
                });
              } else {
                cookieBannerTimerId = window.setTimeout(showBannerSoon, 2800);
              }
            };
            if (typeof document !== "undefined" && document.readyState === "complete") {
              scheduleCheckoutBanner();
            } else if (typeof window !== "undefined") {
              cookieCheckoutLoadHandler = scheduleCheckoutBanner;
              window.addEventListener("load", cookieCheckoutLoadHandler, { once: true });
            } else {
              scheduleCheckoutBanner();
            }
          } else if (onHome) {
            if (typeof window !== "undefined" && "requestIdleCallback" in window) {
              cookieBannerIdleId = window.requestIdleCallback(showBannerSoon, {
                timeout: 2400,
              });
            } else {
              cookieBannerTimerId = window.setTimeout(showBannerSoon, 900);
            }
          } else {
            showBannerSoon();
          }
        });
      });
    }

    // Allow footer / other components to open cookie settings
    const handleOpenCookieSettings = () => {
      setCookieConsentMounted(true);
      setShowCookieModal(true);
      setShowCookieBanner(false);
    };
    window.addEventListener("openCookiePreferences", handleOpenCookieSettings);

    // Event listener for route changes
    const onRouteChangeComplete = (url) => {
      handleRouteChange();
      if (url === "/cart") {
        hydrateCartTotalsFromStorage();
        try {
          const cartOnCartPage = JSON.parse(localStorage.getItem("cart") || "{}");
          if (cartOnCartPage && Object.keys(cartOnCartPage).length > 0) {
            void saveCart(cartOnCartPage, { persistRemote: false });
          }
        } catch {
          // ignore
        }
      }
    };
    router.events.on("routeChangeComplete", onRouteChangeComplete);

    // Clean up the event listener and timeout on component unmount
    return () => {
      router.events.off("routeChangeComplete", onRouteChangeComplete);
      window.removeEventListener("userLoggedIn", handleUserLogin);
      window.removeEventListener("cartPricesSynced", handleCartPricesSynced);
      window.removeEventListener("openCookiePreferences", handleOpenCookieSettings);
      window.removeEventListener("pointerdown", kickoffGeo, true);
      window.removeEventListener("keydown", kickoffGeo, true);
      window.removeEventListener("touchstart", kickoffGeo, true);
      window.removeEventListener("scroll", kickoffGeo, true);
      if (geoFallbackTimer) clearTimeout(geoFallbackTimer);
      if (cookieCheckoutLoadHandler && typeof window !== "undefined") {
        window.removeEventListener("load", cookieCheckoutLoadHandler);
        cookieCheckoutLoadHandler = null;
      }
      if (cookieBannerTimerId) clearTimeout(cookieBannerTimerId);
      if (
        cookieBannerIdleId != null &&
        typeof window !== "undefined" &&
        "cancelIdleCallback" in window
      ) {
        window.cancelIdleCallback(cookieBannerIdleId);
      }
      clearTimeout(timeout);
    };
  }, []); // Empty dependency array ensures this effect runs only once

  // After leaving the tab (hidden → visible) or reconnecting: probe IP once; /api/geo only if IP changed.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let debounceTimer;
    let tabWasHidden = document.visibilityState === "hidden";
    const DEBOUNCE_MS = 1500;
    const schedule = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(
        () => void import("@/lib/geoIP").then((m) => m.refreshGeoIfIpChanged()),
        DEBOUNCE_MS
      );
    };
    const onVisibility = () => {
      const hidden = document.visibilityState === "hidden";
      if (!hidden && tabWasHidden) schedule();
      tabWasHidden = hidden;
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", schedule);
    return () => {
      clearTimeout(debounceTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", schedule);
    };
  }, []);

  const getCartFromDB = async () => {
    try {
      const axios = await loadAxios();
      let user = null;
      try {
        const rawUser = localStorage.getItem("user");
        user = rawUser ? JSON.parse(rawUser) : null;
      } catch {
        user = null;
      }
      let storeCart = {};
      try {
        const raw = localStorage.getItem("cart");
        storeCart = raw ? JSON.parse(raw) : {};
      } catch {
        storeCart = {};
      }
      if (user?.userId && storeCart && Object.keys(storeCart).length == 0) {
        const res = await axios.get(
          `${process.env.NEXT_PUBLIC_BASE_URL}/cart/${user?.userId}`,
          {
            headers: {
              "x-api-key": process.env.NEXT_PUBLIC_API_KEY,
            },
          }
        );
        const data = res?.data;
        if (data && data?.cart) {
          localStorage.setItem("cart", JSON.stringify(data.cart));
          localStorage.setItem("subTotal", JSON.stringify(data.subTotal));
          localStorage.setItem("totalShippingRate", JSON.stringify(data.totalShippingRate));
          localStorage.setItem("fetchedShippingRate", JSON.stringify(data.fetchedShippingRate));
          const { changeCartPrice } = await import("@/lib/geoIP");
          await changeCartPrice(false);
        }
      }
    } catch (err) {
      console.log(err)
    }
  };

  // Meta Pixel: init here only when GTM has not already loaded the same pixel ID.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.fbqInitialized || window.fbqInitScheduled) return;
    window.fbqInitScheduled = true;
    const initPixel = async () => {
      if (window.fbqInitialized) return;
      const { getMetaPixelId, loadMetaPixelScript, initMetaPixel } = await import(
        "@/lib/metaPixel"
      );
      loadMetaPixelScript();
      initMetaPixel(getMetaPixelId());
    };
    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(initPixel, { timeout: 6000 });
      return () => window.cancelIdleCallback(idleId);
    }
    const timer = setTimeout(initPixel, 2500);
    return () => clearTimeout(timer);
  }, []);

  const appChrome = (
    <SidebarProvider>
            <NavigationLoader />
            <main id="main-content">
              <Component
                key={router.asPath}
                cart={cart}
                setCart={setCart}
                addToCart={addToCart}
                addServiceAppointmentToCart={addServiceAppointmentToCart}
                removeFromCart={removeFromCart}
                clearCart={clearCart}
                deleteFromCart={deleteFromCart}
                subTotal={subTotal}
                totalShippingRate={totalShippingRate}
                fetchedShippingRate={fetchedShippingRate}
                saveCart={saveCart}
                userInfoStored={userInfo}
                currencyUser={currency}
                {...pageProps}
              />
            </main>
            <Toaster
              position="bottom-right"
              toastOptions={{
                className: "text-sm font-medium",
              }}
            />
            {cookieConsentMounted ? (
              <CookieConsent
                showBanner={showCookieBanner}
                onAcceptAll={handleAcceptAllCookies}
                onRejectNonEssential={handleRejectNonEssentialCookies}
                onOpenPreferences={() => {
                  setShowCookieModal(true);
                }}
                showModal={showCookieModal}
                onCloseModal={() => setShowCookieModal(false)}
                initialPrefs={cookiePrefs}
                onSavePrefs={handleSaveCookiePreferences}
              />
            ) : null}
            {showDeferredWidgets ? (
              <>
                <FirstOrderDiscountPopup />
                {SHOW_CHATBOT ? <AfomaChatbot /> : null}
              </>
            ) : null}
          </SidebarProvider>
  );

  if (clientRuntimeError) {
    return (
      <>
        <Head>
          <title>Application error | AFOMA</title>
          <meta name="robots" content="noindex" />
        </Head>
        <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16 text-center bg-orange-50">
          <p className="text-lg font-semibold text-blue-950 mb-2">A client error occurred</p>
          <p className="text-xs text-red-900 max-w-xl mb-2 break-words font-mono text-left w-full">
            {clientRuntimeError.message}
          </p>
          {process.env.NEXT_PUBLIC_ENV !== "PROD" && clientRuntimeError.stack ? (
            <details className="mb-4 max-w-3xl w-full text-left">
              <summary className="cursor-pointer text-sm text-blue-800 font-medium">
                Stack trace (DevTools → Console shows the same; STG builds include browser source maps when{" "}
                <code className="text-xs bg-orange-100 px-1">NEXT_PUBLIC_ENV=STG</code> is set for the Vercel build)
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto text-[11px] leading-snug bg-white border border-orange-200 rounded p-3 text-gray-900 whitespace-pre-wrap break-all">
                {clientRuntimeError.stack}
              </pre>
            </details>
          ) : null}
          <p className="text-sm text-gray-600 max-w-md mb-6">
            This screen catches failures that React error boundaries cannot (import-time and many async
            errors). Staging-only issues are often missing{" "}
            <code className="text-xs bg-orange-100 px-1 rounded">NEXT_PUBLIC_*</code> at{" "}
            <code className="text-xs bg-orange-100 px-1 rounded">next build</code> or a cached old
            bundle.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              className="buttonprimary px-6 py-2 rounded-md"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
            <button
              type="button"
              className="px-6 py-2 rounded-md border border-gray-400 text-gray-800"
              onClick={() => setClientRuntimeError(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Head>
        <meta
          name="description"
          content="Shop unique handmade products from global artisans on AFOMA Marketplace, including jewelry, home decor, gifts, and ethical fashion."
        />
      </Head>
      {/* GTM is in pages/_document.js (essential, SSR / view-source). */}
      {/* Microsoft Clarity (heatmaps + session recordings) — analytics consent only */}
      {cookiePrefs?.analytics && analyticsScriptsReady && process.env.NEXT_PUBLIC_CLARITY_ID && (
        <Script
          id="clarity-script"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
              (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
              })(window, document, "clarity", "script", "${process.env.NEXT_PUBLIC_CLARITY_ID}");
            `,
          }}
        />
      )}
      <MountThirdwebContext.Provider value={mountThirdwebContextValue}>
        <div className={`${appShellFontVariablesClassName} app-shell-fonts relative`}>
          <AppErrorBoundary>
            <FramerLazyMotionRoot>
              {mountThirdweb ? (
                <ThirdwebShell>{appChrome}</ThirdwebShell>
              ) : (
                appChrome
              )}
            </FramerLazyMotionRoot>
          </AppErrorBoundary>
        </div>
      </MountThirdwebContext.Provider>
    </>
  );
}
